import {AnimatePresence, animate, motion, useMotionValue} from "framer-motion"
import {FC, useCallback, useRef, useState} from "react"

import {MessageMedia} from "@ri2/db/client"
import {css} from "@styled-system/css"

import {Media} from "./media"

interface Props {
  media: MessageMedia[]
  onSelectMedia: (url: string) => void
  /** Used to generate stable keys; falls back to media.url */
  getKey?: (media: MessageMedia) => string
  /** URL to pass to onSelectMedia for each item (defaults to media.url) */
  getSelectUrl?: (media: MessageMedia) => string
}

/**
 * Horizontal snap-scroll carousel for displaying multiple media items (videos / images).
 * Mirrors the PartsCarousel UX: drag-to-swipe, spring animations, arrow buttons and dot indicators.
 */
export const MediaCarousel: FC<Props> = ({
  media,
  onSelectMedia,
  getKey = (m) => m.url,
  getSelectUrl = (m) => m.url,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const x = useMotionValue(0)

  const GAP = 8
  const CARD_RATIO = 0.85

  /** Returns card width (without gap) and the centering inset. */
  const getLayout = useCallback(() => {
    const container = containerRef.current
    if (!container) return {cardWidth: 280, inset: 0}
    const containerWidth = container.offsetWidth
    const cardWidth = containerWidth * CARD_RATIO
    const inset = (containerWidth - cardWidth) / 2
    return {cardWidth, inset}
  }, [])

  /** Compute the x offset that centers the card at `index`,
   *  but keeps the first card flush-left and the last card flush-right. */
  const getOffsetForIndex = useCallback(
    (index: number) => {
      const {cardWidth, inset} = getLayout()
      const centered = -index * (cardWidth + GAP) + inset
      const totalTrackWidth =
        media.length * cardWidth + (media.length - 1) * GAP
      const containerWidth = containerRef.current?.offsetWidth ?? 0
      const minOffset = -(totalTrackWidth - containerWidth)
      return Math.max(minOffset, Math.min(centered, 0))
    },
    [getLayout, media.length],
  )

  const scrollToIndex = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, media.length - 1))
      setActiveIndex(clamped)
      animate(x, getOffsetForIndex(clamped), {
        type: "spring",
        stiffness: 300,
        damping: 30,
      })
    },
    [media.length, getOffsetForIndex, x],
  )

  const handleDragEnd = useCallback(
    (
      _: MouseEvent | TouchEvent | PointerEvent,
      info: {offset: {x: number}; velocity: {x: number}},
    ) => {
      const {cardWidth} = getLayout()
      const swipeThreshold = cardWidth * 0.2
      const velocityThreshold = 500

      let newIndex = activeIndex
      if (
        info.offset.x < -swipeThreshold ||
        info.velocity.x < -velocityThreshold
      ) {
        newIndex = Math.min(activeIndex + 1, media.length - 1)
      } else if (
        info.offset.x > swipeThreshold ||
        info.velocity.x > velocityThreshold
      ) {
        newIndex = Math.max(activeIndex - 1, 0)
      }

      scrollToIndex(newIndex)
    },
    [activeIndex, media.length, getLayout, scrollToIndex],
  )

  const canGoPrev = activeIndex > 0
  const canGoNext = activeIndex < media.length - 1

  return (
    <div
      ref={containerRef}
      className={css({width: "100%", position: "relative", overflow: "hidden"})}
    >
      {/* Draggable track */}
      <motion.div
        style={{x, display: "flex", gap: GAP}}
        drag="x"
        dragConstraints={{
          left: getOffsetForIndex(media.length - 1),
          right: getOffsetForIndex(0),
        }}
        dragElastic={0.1}
        onDragEnd={handleDragEnd}
        className={css({cursor: "grab", _active: {cursor: "grabbing"}})}
      >
        {media.map((item) => (
          <motion.div
            key={getKey(item)}
            className={css({
              flexShrink: 0,
              width: "85%",
            })}
          >
            <Media
              media={item}
              onClick={() => onSelectMedia(getSelectUrl(item))}
              fill
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Navigation arrows */}
      <AnimatePresence>
        {canGoPrev && (
          <motion.button
            key="prev-arrow"
            initial={{opacity: 0, scale: 0.8}}
            animate={{opacity: 1, scale: 1}}
            exit={{opacity: 0, scale: 0.8}}
            transition={{duration: 0.15}}
            onClick={() => scrollToIndex(activeIndex - 1)}
            aria-label="Previous media"
            className={css({
              position: "absolute",
              left: "4px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              border: "1px solid #E5E7EB",
              backgroundColor: "white",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 2,
              _hover: {
                backgroundColor: "#F9FAFB",
              },
            })}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {canGoNext && (
          <motion.button
            key="next-arrow"
            initial={{opacity: 0, scale: 0.8}}
            animate={{opacity: 1, scale: 1}}
            exit={{opacity: 0, scale: 0.8}}
            transition={{duration: 0.15}}
            onClick={() => scrollToIndex(activeIndex + 1)}
            aria-label="Next media"
            className={css({
              position: "absolute",
              right: "4px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              border: "1px solid #E5E7EB",
              backgroundColor: "white",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 2,
              _hover: {
                backgroundColor: "#F9FAFB",
              },
            })}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Dot indicators */}
      {media.length > 1 && (
        <div
          className={css({
            display: "flex",
            justifyContent: "center",
            gap: "6px",
            marginTop: "8px",
          })}
        >
          {media.map((item, i) => (
            <motion.button
              key={getKey(item)}
              onClick={() => scrollToIndex(i)}
              aria-label={`Go to media ${i + 1}`}
              animate={{
                backgroundColor:
                  i === activeIndex ? "var(--colors-text-primary)" : "#D1D5DB",
                scale: i === activeIndex ? 1.3 : 1,
              }}
              transition={{type: "spring", stiffness: 500, damping: 30}}
              className={css({
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                border: "none",
                padding: 0,
                cursor: "pointer",
              })}
            />
          ))}
        </div>
      )}
    </div>
  )
}
